import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MatSelectChange } from "@angular/material/select";
import { SelectOption } from "../primitives.types";

interface OptionGroup {
  name: string;
  options: SelectOption[];
}

/**
 * Multi-select that keeps the placeholder visible in the field and renders the
 * current selection as removable chips below it (the "tag picker" pattern from
 * the design). Built as its own primitive so the shared `kt-select` stays
 * untouched. Works with `[(ngModel)]` / reactive forms over a `value[]` array.
 *
 * Pass grouped options by setting `group` on each `SelectOption` (e.g.
 * "Facility › Unit") to get hierarchical option headers. When `searchable` is
 * enabled, an in-panel search box filters by both the option label (system name)
 * and its group (facility / unit name).
 */
@Component({
  selector: "kt-tag-select",
  templateUrl: "./tag-select.component.html",
  styleUrls: ["./tag-select.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagSelectComponent),
      multi: true,
    },
  ],
})
export class TagSelectComponent implements ControlValueAccessor, OnChanges {
  @Input() label = "";
  @Input() placeholder = "Select...";
  @Input() options: SelectOption[] = [];
  @Input() disabled = false;
  @Input() hint = "";
  /** When true, renders an in-panel search box that filters by label + group. */
  @Input() searchable = false;
  @Input() searchPlaceholder = "Search...";
  @Input() noResultsText = "No results found";

  @Output() selectionChange = new EventEmitter<any[]>();

  value: any[] = [];
  hasGroups = false;
  groupedOptions: OptionGroup[] = [];
  /** Search-filtered view of `groupedOptions` — what the template renders. */
  filteredGroups: OptionGroup[] = [];
  searchQuery = "";
  /** Stable label lookup so chips render without recomputing per cycle. */
  private labelByValue = new Map<any, string>();

  onChange: (v: any[]) => void = () => {};
  onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    // Only rebuild for a real options change — an unrelated input (label, placeholder)
    // must not churn the panel while it is open.
    if (!changes.options) {
      return;
    }
    const opts = this.options || [];
    this.labelByValue = new Map(opts.map((o) => [o.value, o.label]));
    this.hasGroups = opts.some((o) => !!o.group);
    if (this.hasGroups) {
      const map = new Map<string, SelectOption[]>();
      opts.forEach((o) => {
        const key = o.group || "";
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key).push(o);
      });
      this.groupedOptions = Array.from(map.entries()).map(([name, options]) => ({
        name,
        options,
      }));
    } else {
      this.groupedOptions = [{ name: "", options: opts }];
    }
    this.applyFilter();
  }

  /** Filters groups + their options by the search query (matches group or label). */
  private applyFilter(): void {
    const q = this.searchQuery.toLowerCase().trim();
    if (!q) {
      this.filteredGroups = this.groupedOptions;
      return;
    }
    this.filteredGroups = this.groupedOptions
      .map((g) => {
        // A group whose name matches keeps all its options; otherwise filter options.
        if (g.name && g.name.toLowerCase().includes(q)) {
          return g;
        }
        const options = g.options.filter((o) =>
          o.label.toLowerCase().includes(q)
        );
        return { name: g.name, options };
      })
      .filter((g) => g.options.length > 0);
  }

  onSearch(query: string): void {
    this.searchQuery = query;
    this.applyFilter();
  }

  get hasResults(): boolean {
    return this.filteredGroups.some((g) => g.options.length > 0);
  }

  // `applyFilter` rebuilds the group/option objects, so track by identity of the data
  // (group name, option value) — otherwise every option is re-created and an open
  // panel loses its scroll position.
  trackByGroup(_i: number, group: OptionGroup): string {
    return group.name;
  }

  trackByOption(_i: number, opt: SelectOption): any {
    return opt.value;
  }

  optionLabel(value: any): string {
    return this.labelByValue.get(value) ?? value;
  }

  onSelect(event: MatSelectChange): void {
    // mat-select only reports selection state for options currently rendered in the
    // panel, so a value hidden by the active search filter is absent from
    // `event.value` even though it's still selected. Merge it back in — otherwise
    // filtering, then picking a different option, silently drops it.
    const visible = this.visibleOptionValues();
    const hiddenSelected = (this.value || []).filter((v) => !visible.has(v));
    this.value = [...hiddenSelected, ...(event.value || [])];
    this.onChange(this.value);
    this.onTouched();
    this.selectionChange.emit(this.value);
  }

  private visibleOptionValues(): Set<any> {
    const values = new Set<any>();
    this.filteredGroups.forEach((g) => g.options.forEach((o) => values.add(o.value)));
    return values;
  }

  onPanelToggle(open: boolean): void {
    // Reset the search whenever the panel closes so it reopens clean.
    if (!open && this.searchQuery) {
      this.searchQuery = "";
      this.applyFilter();
    }
  }

  remove(value: any): void {
    this.value = (this.value || []).filter((v) => v !== value);
    this.onChange(this.value);
    this.selectionChange.emit(this.value);
  }

  // ── ControlValueAccessor ─────────────────────────────────────────────────────
  writeValue(value: any[]): void {
    this.value = value || [];
  }

  registerOnChange(fn: (v: any[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
